import React, { useState, useEffect } from 'react';
import LoadingAnimation from './LoadingAnimation';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GIRAFFE_MASCOT = '/images/simby-mascote.png';

const HUMOR_OPTIONS = [
  { score: 5, key: 'leve_e_presente', label: 'Muito bem',     hex: '#4A7C59' },
  { score: 4, key: 'tranquila',       label: 'Bem',           hex: '#7FB069' },
  { score: 3, key: 'neutra',          label: 'Mais ou menos', hex: '#F5A623' },
  { score: 2, key: 'sobrecarregada',  label: 'Mal',           hex: '#D97706' },
  { score: 1, key: 'pesada',          label: 'Muito mal',     hex: '#B45309' },
];

const ESTADO_TO_SCORE = {
  leve_e_presente: 5, tranquila: 4, neutra: 3, sobrecarregada: 2, pesada: 1,
};

const NECESSIDADES_CHIPS = [
  { categoria: 'Conexão',        chips: ['pertencimento', 'ser ouvida', 'intimidade', 'apoio'] },
  { categoria: 'Autonomia',      chips: ['escolha', 'espaço', 'independência', 'respeito'] },
  { categoria: 'Segurança',      chips: ['estabilidade', 'clareza', 'previsibilidade'] },
  { categoria: 'Reconhecimento', chips: ['valorização', 'contribuição', 'ser vista'] },
  { categoria: 'Descanso',       chips: ['silêncio', 'recuperação', 'leveza', 'pausa'] },
  { categoria: 'Criação',        chips: ['sentido', 'propósito', 'expressão', 'aprendizado'] },
];

const GATILHOS_LIST = [
  { key: 'fome',       label: 'Fome',            icon: '🍽️' },
  { key: 'sono',       label: 'Sono / cansaço',  icon: '😴' },
  { key: 'exaustao',   label: 'Sobrecarga',      icon: '📋' },
  { key: 'tensao',     label: 'Tensão no corpo', icon: '😤' },
  { key: 'ciclo',      label: 'Ciclo / TPM',     icon: '🌙' },
  { key: 'isolamento', label: 'Isolamento',      icon: '🫥' },
  { key: 'conflito',   label: 'Conflito aberto', icon: '⚡' },
];

const SAFETY_MSG = 'Você chegou pesada alguns dias seguidos. Isso é válido — mas apoio profissional pode fazer diferença. Estou aqui com você.';

// Overlay steps in order; index 0 = base (screen1)
const STEP_ORDER = ['screen1', 'bloco_a', 'bloco_b', 'bloco_c'];

// ── Simby speech bubble ────────────────────────────────────────────────────────
const SpeechBubble = ({ text, compact }) => (
  <div className="flex items-start gap-3 mb-5">
    <img
      src={GIRAFFE_MASCOT} alt="Simby"
      className={`${compact ? 'w-10 h-14' : 'w-14 h-20'} object-contain flex-shrink-0`}
    />
    <div className="relative flex-1">
      <div className="absolute left-[-8px] top-4 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-purple-100" />
      <div className="bg-purple-100 rounded-2xl p-4">
        <p className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-900 leading-snug`}>
          {text}
        </p>
      </div>
    </div>
  </div>
);

// ── Slide-in overlay wrapper ───────────────────────────────────────────────────
const Overlay = ({ visible, children }) => (
  <div className={`
    absolute inset-0 p-6 flex flex-col
    bg-gradient-to-br from-white to-purple-50
    transition-transform duration-300 ease-in-out
    ${visible ? 'translate-x-0' : 'translate-x-full'}
  `}>
    {children}
  </div>
);

// ── Bloco top bar: back button + humor indicator ───────────────────────────────
const BlocoHeader = ({ humor, onBack }) => (
  <div className="flex items-center gap-3 mb-4 flex-shrink-0">
    <button
      onClick={onBack}
      className="text-sm text-gray-400 hover:text-gray-700 transition-colors flex items-center gap-1"
    >
      ← Voltar
    </button>
    {humor && (
      <div className="ml-auto flex items-center gap-1.5">
        <div style={{ backgroundColor: humor.hex }} className="w-2.5 h-2.5 rounded-full" />
        <span className="text-xs text-gray-500">{humor.label}</span>
      </div>
    )}
  </div>
);

// ── Bloco bottom nav: skip + primary action ────────────────────────────────────
const BlocoFooter = ({ onSkip, onNext, nextLabel = 'Próxima →', isSubmitting = false }) => (
  <div className="flex-shrink-0 pt-3">
    <Button
      onClick={onNext}
      disabled={isSubmitting}
      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold"
    >
      {isSubmitting ? (
        <span className="flex items-center justify-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          Salvando...
        </span>
      ) : nextLabel}
    </Button>
    {onSkip && (
      <button
        onClick={onSkip}
        className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-2 transition-colors"
      >
        Pular
      </button>
    )}
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const DailyCheckIn = ({ onComplete, compact = false, currentLessonId = null, postCheckinInsight = null }) => {
  const [step, setStep]                         = useState('loading');
  const [selectedHumor, setSelectedHumor]       = useState(null);
  const [situacaoTexto, setSituacaoTexto]        = useState('');
  const [necessidadeTexto, setNecessidadeTexto]  = useState('');
  const [gatilhos, setGatilhos]                 = useState({
    fome: false, sono: false, exaustao: false,
    tensao: false, ciclo: false, isolamento: false, conflito: false,
  });
  const [nenhum, setNenhum]                     = useState(false);
  const [pendingInsight, setPendingInsight]      = useState(null);
  const [localInsight, setLocalInsight]          = useState(postCheckinInsight);
  const [safetyAlert, setSafetyAlert]            = useState(null);
  const [safetyDismissed, setSafetyDismissed]    = useState(false);
  const [isSubmitting, setIsSubmitting]          = useState(false);
  const [doneCheckin, setDoneCheckin]            = useState(null);

  useEffect(() => { fetchTodayStatus(); }, []);

  const fetchTodayStatus = async () => {
    try {
      const res = await axios.get(`${API}/emotion-checkin/today`, { withCredentials: true });
      const { has_checked_in, checkin, pending_insight, safety_alert } = res.data;
      if (safety_alert) setSafetyAlert(safety_alert);
      if (pending_insight) {
        setPendingInsight(pending_insight);
        setStep('insight');
      } else if (has_checked_in && checkin?.etapa_concluida !== 'pulado') {
        setDoneCheckin(checkin);
        try {
          const insightRes = await axios.get(`${API}/emotion-checkin/insights/last`, { withCredentials: true });
          if (insightRes.data?.insight) setLocalInsight(insightRes.data.insight);
        } catch {}
        setStep('done');
      } else {
        setStep('screen1');
      }
    } catch {
      setStep('screen1');
    }
  };

  const handleInsightContinue = async () => {
    try {
      await axios.post(
        `${API}/emotion-checkin/mark-insight-shown/${pendingInsight.einsight_id}`,
        {}, { withCredentials: true }
      );
    } catch {}
    setPendingInsight(null);
    setStep('screen1');
  };

  const handleHumorSelect = (option) => {
    setSelectedHumor(option);
    setStep('bloco_a');
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/emotion-checkin/skip`, {}, { withCredentials: true });
    } catch {}
    setStep('done');
    if (onComplete) onComplete(null);
  };

  const handleChipClick = (chip) => {
    const sep = necessidadeTexto.trim() ? ', ' : '';
    setNecessidadeTexto(prev => prev.trim() ? prev.trim() + sep + chip : chip);
  };

  const toggleGatilho = (key) => {
    setNenhum(false);
    setGatilhos(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleNenhum = () => {
    const next = !nenhum;
    setNenhum(next);
    if (next) setGatilhos({ fome: false, sono: false, exaustao: false, tensao: false, ciclo: false, isolamento: false, conflito: false });
  };

  const hasAnyDeepening = () =>
    situacaoTexto.trim() || necessidadeTexto.trim() ||
    Object.values(gatilhos).some(Boolean) || nenhum;

  const handleConcluir = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/emotion-checkin/submit`,
        {
          humor_score:        selectedHumor.score,
          estado:             selectedHumor.key,
          etapa_concluida:    hasAnyDeepening() ? 'com_aprofundamento' : 'checkin_apenas',
          situacao_texto:     situacaoTexto.trim() || null,
          necessidade_texto:  necessidadeTexto.trim() || null,
          gatilho_fome:       gatilhos.fome,
          gatilho_sono:       gatilhos.sono,
          gatilho_exaustao:   gatilhos.exaustao,
          gatilho_tensao:     gatilhos.tensao,
          gatilho_ciclo:      gatilhos.ciclo,
          gatilho_isolamento: gatilhos.isolamento,
          gatilho_conflito:   gatilhos.conflito,
          licao_atual_id:     currentLessonId || null,
        },
        { withCredentials: true }
      );
      if (res.data?.simby_message) setLocalInsight(res.data.simby_message);
    } catch (e) {
      console.error('Error submitting check-in:', e);
    } finally {
      setIsSubmitting(false);
    }
    setStep('done');
    if (onComplete) onComplete(selectedHumor.key);
  };

  // ── Overlay visibility ─────────────────────────────────────────────────────
  const currentStepIdx = STEP_ORDER.indexOf(step);
  const isOverlayOn = (overlayStep) => {
    if (step === 'done') return false;
    const idx = STEP_ORDER.indexOf(overlayStep);
    return idx > 0 && currentStepIdx >= idx;
  };

  // ── Done humor ─────────────────────────────────────────────────────────────
  const doneScore  = doneCheckin?.humor_score || ESTADO_TO_SCORE[doneCheckin?.estado] || selectedHumor?.score || 3;
  const doneOption = HUMOR_OPTIONS.find(o => o.score === doneScore) || HUMOR_OPTIONS[2];

  return (
    <div className="relative h-full min-h-[360px]">

      {/* ── Loading ──────────────────────────────────────────────────────────── */}
      {step === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingAnimation size="sm" />
        </div>
      )}

      {/* ── BASE LAYER: screen1 or done ──────────────────────────────────────── */}
      {step !== 'loading' && (
        <div className="absolute inset-0 p-6 flex flex-col">
          {step === 'done' ? (
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-start gap-3">
                <div
                  style={{ backgroundColor: doneOption.hex }}
                  className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                >
                  <span className="text-white font-bold text-xs text-center leading-tight px-1">
                    {doneOption.label.split(' ')[0]}
                  </span>
                </div>
                <div className="flex-1">
                  {localInsight ? (
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="text-base">🌿</span>
                        <span className="text-xs font-semibold text-green-800 uppercase tracking-wide">
                          O que a Simby vem te dizendo
                        </span>
                      </div>
                      {localInsight.insights?.length ? (
                        <div className="space-y-2">
                          {localInsight.insights.map((c, i) => (
                            <div key={i} className="bg-white border border-green-200 rounded-xl p-3">
                              <p className="text-sm font-semibold text-green-900">{c.titulo}</p>
                              <p className="text-sm text-gray-700 mt-0.5 leading-snug">{c.descricao}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-800 leading-relaxed italic">
                          "{localInsight.texto}"
                        </p>
                      )}
                      {localInsight.created_at && (
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(localInsight.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit', month: 'long'
                          })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        <img src={GIRAFFE_MASCOT} alt="Simby" className="w-7 h-9 object-contain flex-shrink-0" />
                        <p className="text-sm text-gray-800 leading-relaxed">
                          Registrado. Obrigada por parar e se olhar hoje — isso já é prática.
                        </p>
                      </div>
                    </div>
                  )}
                  {doneCheckin && (
                    <button
                      onClick={() => { setDoneCheckin(null); setStep('screen1'); }}
                      className="mt-2 text-xs text-gray-400 hover:text-gray-600 underline-offset-2 hover:underline"
                    >
                      Fazer um novo registro
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              {safetyAlert && !safetyDismissed && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 relative flex-shrink-0">
                  <p className="text-sm text-red-800 leading-relaxed pr-6">{SAFETY_MSG}</p>
                  <button
                    onClick={() => setSafetyDismissed(true)}
                    className="absolute top-3 right-3 text-red-400 hover:text-red-600 text-lg"
                    aria-label="Fechar alerta"
                  >×</button>
                </div>
              )}
              <SpeechBubble text="Como você está hoje?" compact={compact} />
              <div className={`flex ${compact ? 'gap-2' : 'gap-2 md:gap-3'} mb-4`}>
                {HUMOR_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => handleHumorSelect(opt)}
                    aria-label={opt.label}
                    style={{ backgroundColor: opt.hex }}
                    className={`
                      flex-1 rounded-xl text-white font-semibold
                      transition-all duration-150 hover:opacity-90 hover:scale-105
                      focus:outline-none focus:ring-2 focus:ring-offset-2
                      ${compact ? 'py-3 text-xs' : 'py-4 text-sm'}
                    `}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="text-center mt-auto">
                <button
                  onClick={handleSkip}
                  className="text-sm text-gray-400 hover:text-gray-600 underline-offset-2 hover:underline transition-colors"
                >
                  Pular por hoje
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── OVERLAY: Insight ─────────────────────────────────────────────────── */}
      <Overlay visible={step === 'insight'}>
        {pendingInsight && (
          <>
            <div className="flex-1 flex flex-col justify-center">
              <div className="p-4 bg-green-50 border border-green-200 rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🌿</span>
                  <span className="text-xs font-semibold text-green-800 uppercase tracking-wide">
                    O que os seus registros mostram
                  </span>
                </div>
                {pendingInsight.insights?.length ? (
                  <div className="space-y-2">
                    {pendingInsight.insights.map((c, i) => (
                      <div key={i} className="bg-white border border-green-200 rounded-xl p-3">
                        <p className="text-sm font-semibold text-green-900">{c.titulo}</p>
                        <p className="text-sm text-gray-700 mt-0.5 leading-snug">{c.descricao}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-800 leading-relaxed italic">
                    "{pendingInsight.texto}"
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={handleInsightContinue}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold mt-4 flex-shrink-0"
            >
              Continuar para o check-in
            </Button>
          </>
        )}
      </Overlay>

      {/* ── OVERLAY: Bloco A — O que aconteceu? ──────────────────────────────── */}
      <Overlay visible={isOverlayOn('bloco_a')}>
        <BlocoHeader humor={selectedHumor} onBack={() => setStep('screen1')} />
        <p className="text-sm font-semibold text-purple-800 mb-1 flex-shrink-0">
          Houve algo específico que te deixou assim?
        </p>
        <p className="text-xs text-gray-400 mb-3 flex-shrink-0">
          Esse é um espaço só seu — conte se quiser.
        </p>
        <textarea
          value={situacaoTexto}
          onChange={e => setSituacaoTexto(e.target.value)}
          maxLength={500}
          placeholder="Pode ser uma situação, uma fala, um momento..."
          className="flex-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-purple-300"
        />
        <p className="text-xs text-gray-400 text-right mt-1 flex-shrink-0">{situacaoTexto.length}/500</p>
        <BlocoFooter onSkip={() => setStep('bloco_b')} onNext={() => setStep('bloco_b')} />
      </Overlay>

      {/* ── OVERLAY: Bloco B — Que necessidade? ──────────────────────────────── */}
      <Overlay visible={isOverlayOn('bloco_b')}>
        <BlocoHeader humor={selectedHumor} onBack={() => setStep('bloco_a')} />
        <p className="text-sm font-semibold text-purple-800 mb-2 flex-shrink-0">
          Tem alguma necessidade pedindo atenção?
        </p>
        <textarea
          value={necessidadeTexto}
          onChange={e => setNecessidadeTexto(e.target.value)}
          maxLength={300}
          placeholder="Escreva ou use os chips abaixo..."
          rows={2}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-purple-300 flex-shrink-0"
        />
        <p className="text-xs text-gray-400 text-right mt-1 mb-2 flex-shrink-0">{necessidadeTexto.length}/300</p>
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-wrap gap-1.5 pb-1">
            {NECESSIDADES_CHIPS.map(({ chips }) =>
              chips.map(chip => (
                <button
                  key={chip}
                  onClick={() => handleChipClick(chip)}
                  className="text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-full px-2.5 py-1 transition-colors"
                >
                  {chip}
                </button>
              ))
            )}
          </div>
        </div>
        <BlocoFooter onSkip={() => setStep('bloco_c')} onNext={() => setStep('bloco_c')} />
      </Overlay>

      {/* ── OVERLAY: Bloco C — Seu corpo? ────────────────────────────────────── */}
      <Overlay visible={isOverlayOn('bloco_c')}>
        <BlocoHeader humor={selectedHumor} onBack={() => setStep('bloco_b')} />
        <p className="text-sm font-semibold text-purple-800 mb-3 flex-shrink-0">
          Seu corpo está te dando algum sinal?
        </p>
        <div className="flex-1 grid grid-cols-2 gap-1.5 content-start">
          {GATILHOS_LIST.map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => toggleGatilho(key)}
              className={`flex items-center gap-1.5 text-left rounded-lg px-2.5 py-2 text-xs border transition-all ${
                gatilhos[key]
                  ? 'bg-purple-50 border-purple-400 text-purple-800 font-medium'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{icon}</span>
              <span className="leading-tight">{label}</span>
            </button>
          ))}
          <button
            onClick={toggleNenhum}
            className={`col-span-2 flex items-center gap-1.5 text-left rounded-lg px-2.5 py-2 text-xs border transition-all ${
              nenhum
                ? 'bg-gray-50 border-gray-400 text-gray-700 font-medium'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <span>✓</span>
            <span>Nenhum desses</span>
          </button>
        </div>
        <BlocoFooter onNext={handleConcluir} nextLabel="Concluir" isSubmitting={isSubmitting} />
      </Overlay>

    </div>
  );
};

export default DailyCheckIn;
